import React, { useState } from "react";
import apiClient from "../../../../../api/apiClient";

const TYPES = ["note", "call", "email", "meeting", "task"];

const EMPTY = {
    type: "note",
    subject: "",
    body: "",
    outcome: "",
    scheduledAt: "",
    dueAt: "",
    isDone: false,
};

const AddActivityModal = ({ leadId, companyId, activity, onClose, onSaved }) => {
    const [form, setForm] = useState(activity ? { ...EMPTY, ...activity } : { ...EMPTY });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));
    const setCheck = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.checked }));
    
    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setSaving(true);
        try {
            const payload = { ...form };
            ["outcome", "scheduledAt", "dueAt"].forEach((k) => {
                if (!payload[k]) payload[k] = null;
            });

            if (activity) {
                const editUrl = companyId
                    ? `/admin/crm/company-activities/${activity.id}`
                    : `/admin/crm/activities/${activity.id}`;
                await apiClient.put(editUrl, payload);
                onSaved(null);
            } else {
                const createUrl = companyId
                    ? `/admin/crm/companies/${companyId}/activities`
                    : `/admin/crm/leads/${leadId}/activities`;
                const res = await apiClient.post(createUrl, payload);
                onSaved(res.data.activity);
            }
        } catch (err) {
            const d = err.response?.data;
            const msg = d?.error
                || (Array.isArray(d?.errors) ? d.errors[0]?.msg : null)
                || (typeof d === "string" ? null : null)
                || err.message
                || "Failed to save activity";
            setError(msg);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="crm-modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
            <div className="crm-modal">
                <div className="crm-modal__header">
                    <h2 className="crm-modal__title">{activity ? "Edit Activity" : "Log Activity"}</h2>
                    <button className="crm-modal__close" onClick={onClose}>×</button>
                </div>
                <form onSubmit={handleSubmit}>
                    <div className="crm-modal__body">
                        {error && (
                            <div className="crm-email-banner crm-email-banner--error" style={{ marginBottom: 16 }}>
                                {error}
                            </div>
                        )}

                        <div className="admin-form-group">
                            <label className="admin-form-label">Type</label>
                            <div className="crm-type-btn-group">
                                {TYPES.map((t) => (
                                    <button
                                        key={t}
                                        type="button"
                                        className={`crm-type-btn ${form.type === t ? "active" : ""}`}
                                        onClick={() => setForm((f) => ({ ...f, type: t }))}
                                    >
                                        {t.charAt(0).toUpperCase() + t.slice(1)}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="admin-form-group">
                            <label className="admin-form-label">
                                Subject <span style={{ color: "#ef4444" }}>*</span>
                            </label>
                            <input
                                className="admin-form-input"
                                required
                                value={form.subject}
                                onChange={set("subject")}
                                maxLength={300}
                                placeholder="e.g. Initial discovery call"
                                autoFocus
                            />
                        </div>

                        <div className="admin-form-group">
                            <label className="admin-form-label">Notes</label>
                            <textarea
                                className="admin-form-input"
                                rows={4}
                                value={form.body}
                                onChange={set("body")}
                                style={{ resize: "vertical" }}
                                placeholder="Add any details or notes…"
                            />
                        </div>

                        <div className="admin-form-group">
                            <label className="admin-form-label">Outcome</label>
                            <input
                                className="admin-form-input"
                                value={form.outcome}
                                onChange={set("outcome")}
                                maxLength={300}
                                placeholder="e.g. Left voicemail, Agreed to demo…"
                            />
                        </div>

                        {(form.type === "call" || form.type === "meeting") && (
                            <div className="admin-form-group">
                                <label className="admin-form-label">Scheduled At</label>
                                <input
                                    className="admin-form-input"
                                    type="datetime-local"
                                    value={form.scheduledAt ? form.scheduledAt.slice(0, 16) : ""}
                                    onChange={set("scheduledAt")}
                                />
                            </div>
                        )}

                        {form.type === "task" && (
                            <div className="admin-form-row">
                                <div className="admin-form-group">
                                    <label className="admin-form-label">Due Date</label>
                                    <input
                                        className="admin-form-input"
                                        type="datetime-local"
                                        value={form.dueAt ? form.dueAt.slice(0, 16) : ""}
                                        onChange={set("dueAt")}
                                    />
                                </div>
                                <div className="admin-form-group crm-task-done-row">
                                    <input
                                        id="isDone"
                                        type="checkbox"
                                        className="crm-checkbox"
                                        checked={!!form.isDone}
                                        onChange={setCheck("isDone")}
                                    />
                                    <label htmlFor="isDone" className="admin-form-label" style={{ cursor: "pointer", marginBottom: 0 }}>
                                        Mark as done
                                    </label>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="crm-modal__footer">
                        <button type="button" className="admin-btn" onClick={onClose} disabled={saving}>
                            Cancel
                        </button>
                        <button type="submit" className="admin-btn admin-btn--primary" disabled={saving}>
                            {saving ? "Saving…" : activity ? "Save Changes" : "Log Activity"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AddActivityModal;
